import {useState, useRef, useEffect} from 'react';
import {CommunicationManager} from './Tools/CommunicationManager';
import SearchTermSuggestions from './Component/SearchTermSuggestions';
import NewsSearchResults from './Component/NewsSearchResults';
import TopicSearchResults from "./Component/TopicSearchResults";
import LawSearchResults from "./Component/LawSearchResults";
import LegislativeAdviceResults from "./Component/LegislativeAdviceResults";
import DebugPanel from "./Component/DebugPanel";

// Enable debug mode via URL param ?search_debug=1 (persists to localStorage)
// or manually: localStorage.setItem('nova_search_debug', '1'); location.reload();
// Disable: localStorage.removeItem('nova_search_debug'); location.reload();
// Note: debug mode also requires the current Drupal user to have admin permissions.
(function activateDebugFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('search_debug') === '1') {
    localStorage.setItem('nova_search_debug', '1');
  }
})();
const DEBUG_REQUESTED = localStorage.getItem('nova_search_debug') === '1';

const ICON_PATH = '/themes/custom/nova/templates/includes/_icons.svg';

export default function MainSearchApp() {
  const fadeTime = 700;
  const fadeTimeClass = `duration-[${fadeTime}ms]`;

  const searchInputRef = useRef(null);
  const websiteSectionRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [websiteSection, setWebsiteSection] = useState('');

  const [queryString, setQueryString] = useState('');

  const [searchFocus, setSearchFocus] = useState(false);
  const [renderDom, setRenderDom] = useState(false);
  const [sections, setSections] = useState(null);
  const [topicResults, setTopicResults] = useState([]);
  const [newsResults, setNewsResults] = useState([]);
  const [lawResults, setLawResults] = useState([]);
  const [legislativeAdviceResults, setLegislativeAdviceResults] = useState([]);
  const [topMostSearched, setTopMostSearched] = useState([]);
  const [suggestion, setSuggestion] = useState(null);

  const [searchRequestTimeout, setSearchRequestTimeout] = useState(null);

  // Debug state — only active when localStorage flag is set AND server confirms admin
  const [debugMode, setDebugMode] = useState(false);
  const [debugRequestUrl, setDebugRequestUrl] = useState(null);
  const [debugResponse, setDebugResponse] = useState(null);
  const [debugDurationMs, setDebugDurationMs] = useState(null);
  const [debugEsQueries, setDebugEsQueries] = useState(null);
  const [debugAutocomplete, setDebugAutocomplete] = useState(null);

  const commManager = new CommunicationManager('headless_search');

  // Once a suggestion has been set (by clicking on a suggestion) submit the form afterwards so that we navigate to the SearchAPI Results page
  useEffect(() => {
    if (suggestion !== null) {
      document.getElementById('search_app_submit_button').click();
    }
  }, [suggestion]);

  function useSuggestion(suggestion) {
    // This will update the search term field
    setSearchTerm(suggestion);

    // Setting the suggestion will trigger the useEffect above, which will then trigger a form submit
    setSuggestion(suggestion);
  }

  function shoutSearchQuery(query) {
    console.debug('Sending [' + query + '] to datalayer as event flink_search_query')
    window.dataLayer?.push({
      'event': 'flink_search_query',
      'fsq_query': query
    });
  }

  // Steal focus from whatever was triggering the app
  if (renderDom === true && searchInputRef.current) {
    searchInputRef.current.focus();
  }

  // Pass down renderDom to the showContents, which will then fade in using a class switch
  let showContents = renderDom;

  if (searchTerm.length < 2) {
    // Reset the results if there were any; without the if-statement we would cause a render loop
    if (topicResults.length) {
      setTopicResults([]);
    }
    // Reset the results if there were any; without the if-statement we would cause a render loop
    if (newsResults.length) {
      setNewsResults([]);
    }

  } else {
    let newQueryString = 'q=' + encodeURIComponent(searchTerm);

    if (websiteSection.length) {
      newQueryString += '&section=' + websiteSection;
    }

    if (newQueryString !== queryString) {
      // Update query-string; this will execute the useEffect below which will execute the API call
      setQueryString(newQueryString);
    }
  }

  /**
   * Callback for searchterm input field and website section field (clean wildcard characters before setting it)
   * @param e
   */
  function handleSearch(e) {
    let searchTerm = searchInputRef.current.value.replace('*', '');
    let websiteSection = websiteSectionRef.current.value;

    // Let other (react) components know about the current searchTerm
    commManager.sendMessage({searchTerm, websiteSection});

    setSearchTerm(searchTerm);
    setWebsiteSection(websiteSection);
  }

  /**
   * Submit on enter
   * @param e
   */
  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      useSuggestion(e.target.value);
    }
  }

  // Execute this only during initial load
  useEffect(() => {
    commManager.receiveMessage((message) => {
      if ('searchTerm' in message) {
        console.debug('Received searchTerm ' + message.searchTerm);
        setSearchTerm(message.searchTerm);
      }
      if ('websiteSection' in message) {
        console.debug('Received websiteSection ' + message.websiteSection);
        setWebsiteSection(message.websiteSection);
      }
    });
  },[]);

  // Execute this only during initial load
  useEffect(() => {
    let isMounted = true;

    fetch('/api/search/initialize')
      .then(response => response.json())
      .then(data => {
        if (isMounted) {
          setTopMostSearched(data.top_most_searched);
          setSections(data.section_option_groups);
          if (DEBUG_REQUESTED && data.debug_allowed) {
            setDebugMode(true);
          }
        }
      });

    return () => {isMounted = false;};
  },[]);

  useEffect(() => {
    if (!queryString) {
      return;
    }

    let isMounted = true;

    if (searchRequestTimeout) {
      clearInterval(searchRequestTimeout);
    }

    setSearchRequestTimeout(setTimeout(() => {
      const url = '/api/search/public?' + queryString + (debugMode ? '&_debug=1' : '');
      const fetchStart = Date.now();

      if (debugMode) {
        setDebugRequestUrl('/api/search/public?' + queryString);
        setDebugResponse(null);
        setDebugDurationMs(null);
        setDebugEsQueries(null);
      }

      fetch(url)
        .then(response => response.json())
        .then(data => {
          if (isMounted) {
            setTopicResults(data.topics?.data?.results ?? []);
            setNewsResults(data.news?.data?.results ?? []);
            setLawResults(data.books?.data?.results ?? []);
            setLegislativeAdviceResults(data.legislative_advices?.data?.results ?? []);
            shoutSearchQuery(searchTerm);

            if (debugMode) {
              setDebugResponse(data);
              setDebugDurationMs(Date.now() - fetchStart);
              setDebugEsQueries(data._debug?.queries ?? null);
            }
          }
        });
    }, 300));

    return () => {isMounted = false;};

  }, [queryString, searchTerm]);

  return (
    <>
      {debugMode && (
        <DebugPanel
          requestUrl={debugRequestUrl}
          response={debugResponse}
          durationMs={debugDurationMs}
          esQueries={debugEsQueries}
          autocomplete={debugAutocomplete}
        />
      )}
      <form action="/zoeken" data-fcs-region="search_app"
        className={'search-block__search-app ' + (searchTerm.length ? 'search-block__search-app--is-active' : '') + (searchTerm.length > 2 ? ' search-block__search-app--is-searching' : '')}>
        <div className="search-app__search-app-form">
          <div className="form--app">
            <div className="form-item">
              <div className="main-search main-search--overlay">
                <div className="main-search__input-wrapper">
                  <label htmlFor="overlay-search-input">Zoek op trefwoord</label>
                  <input type="search" id="overlay-search-input" name="keys" onKeyDown={handleKeyDown}
                         title="Geef de woorden op waarnaar u wilt zoeken."
                         placeholder="Zoeken..." onChange={handleSearch} ref={searchInputRef} value={searchTerm}
                         className="main-search__input form-search" role="presentation" autoComplete="off"
                         autoCorrect="off" spellCheck="false"/>
                </div>

                {sections &&
                  <div className={'main-search__filter-wrapper'}>
                    <label htmlFor={'overlay-search-filter'}>Zoekgebied</label>
                    <select id={'overlay-search-filter'} className={'main-search__filter'} name="section" ref={websiteSectionRef} value={websiteSection} onChange={handleSearch}>
                      {Object.entries(sections).map(([groupKey, items], groupIndex) => (
                        <optgroup label={groupKey} key={groupKey}>
                          {Object.entries(items).map(([key, value]) => (
                            <option value={key} key={key}>{value}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                }

                <button type="submit"
                        className="main-search__submit-wrapper button button--primary"
                        data-fcs-elm-type="submit">
                  <span className="button__text">Zoeken</span>
                  <span className="button__icon">
                    <svg className="icon">
                      <use href={`${ICON_PATH}#search`}/>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {topMostSearched.length > 0 &&
          <div className={"search-app__popular"}>
            <h3 className="search-app__popular-heading">Veel gezocht:</h3>
            <ul className="search-app__popular-list">
              {topMostSearched.map((search_term, index) =>
                <li className={"search-app__popular-item"} key={index} data-fcs-region="most_searched">
                  <a className={"search-app__popular-link"}
                     href={"/zoeken?keys=" + encodeURIComponent(search_term)}>{search_term}</a>
                </li>
              )}
            </ul>
          </div>
        }

        <div className={"search-app__results"}>
          {searchTerm.length > 2 &&
            <ul className="search-results">

              <SearchTermSuggestions searchTerm={searchTerm} useSuggestion={useSuggestion} onDebugData={debugMode ? setDebugAutocomplete : null}/>

              {topicResults.length > 0 &&
                <li className={"search-results__item"} key={'topic-results'} data-fcs-region="topic_results">
                  <h3 className={"search-results__heading"}>Onderwerpen</h3>
                  <TopicSearchResults searchTerm={searchTerm} searchResults={topicResults} limit={3}/>
                </li>
              }

              {newsResults.length > 0 &&
                <li className={"search-results__item"} key={'news-results'} data-fcs-region="news_results">
                  <h3 className={"search-results__heading"}>Actueel</h3>
                  <NewsSearchResults searchTerm={searchTerm} searchResults={newsResults} limit={3}/>
                </li>
              }

              {lawResults.length > 0 &&
                <li className={"search-results__item"} key={'law-results'} data-fcs-region="law_results">
                  <h3 className={"search-results__heading"}>Wet- en regelgeving</h3>
                  <LawSearchResults searchTerm={searchTerm} searchResults={lawResults} limit={3}/>
                </li>
              }

              {legislativeAdviceResults.length > 0 &&
                <li className={"search-results__item"} key={'legislative-advice-results'} data-fcs-region="legislative_advice_results">
                  <h3 className={"search-results__heading"}>Wetgevingsadviezen</h3>
                  <LegislativeAdviceResults searchTerm={searchTerm} searchResults={legislativeAdviceResults} limit={3}/>
                </li>
              }

            </ul>
          }
        </div>

        {searchTerm.length > 0 &&
          <button type="submit" id="search_app_submit_button"
                  className="button button--as-link" data-fcs-elm-type="submit">
            <span className="button__text">Toon alle resultaten</span>
            <span className="button__icon">
              <svg className="icon">
                <use href={`${ICON_PATH}#arrow-right-square`}/>
              </svg>
            </span>
          </button>
        }
      </form>
    </>
  );
}
